import { adminApi, type AdminUserDto } from '../../services/admin.api';
import { type BillingPlanDto } from '../../services/billing.api';
import { ApiError } from '../../services/core.api';
import { brandIcon, icon } from '../../icons';
import { t } from '../../services/i18n';
import { esc, formatDate, renderBlockedBadge, renderRoleBadge } from './admin-shared';

type OpenAdminUserDetailsOptions = {
  user: AdminUserDto;
  plans: BillingPlanDto[];
  plansError?: string;
  onSaved?: () => Promise<void>;
  onDeleted?: () => Promise<void>;
};

export function openAdminUserDetails(options: OpenAdminUserDetailsOptions): void {
  const { user, plans, plansError = '', onSaved, onDeleted } = options;
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  const isAdminRole = user.role === 1 || String(user.role).toLowerCase() === 'admin';

  const providerIcon = user.provider === 'google'
    ? brandIcon('google', { size: 14 })
    : user.provider === 'microsoft'
      ? brandIcon('microsoft', { size: 14 })
      : icon('mail', { size: 14 });
  const roleLabel = renderRoleBadge(user.role);
  const blockedLabel = renderBlockedBadge(user.isBlocked);
  const createdAt = formatDate(user.createdAt);
  const lastLoggedIn = user.lastLoggedIn ? formatDate(user.lastLoggedIn) : '&mdash;';
  const onboardingText = user.onboardingCompleted ? t('admin.completed') : t('admin.notCompleted');
  const businessText = user.isBusinessAccount ? t('admin.yes') : t('admin.no');
  const currentPlanLabel = resolvePlanLabel(user.subscriptionPlanKey, plans);
  const currentStatusLabel = translateSubscriptionStatus(user.subscriptionStatus);
  const currentIntervalLabel = translateInterval(user.subscriptionInterval);
  const currentProviderLabel = user.paymentProviderKey ? esc(user.paymentProviderKey) : t('admin.subscription.none');
  const currentCustomerIdLabel = user.paymentProviderCustomerId ? esc(user.paymentProviderCustomerId) : t('admin.subscription.none');
  const currentStartLabel = user.subscriptionStartedAtUtc ? formatDate(user.subscriptionStartedAtUtc) : t('admin.subscription.none');
  const currentEndLabel = user.subscriptionCurrentPeriodEndUtc ? formatDate(user.subscriptionCurrentPeriodEndUtc) : t('admin.subscription.none');
  const subscriptionStartStatusLabel = user.newSubscriptionBlocked ? t('admin.subscription.startBlocked') : t('admin.subscription.startAllowed');
  const initialPlanKey = resolveInitialPlanKey(user, plans);
  const initialInterval = user.subscriptionInterval === 'yearly' ? 'yearly' : 'monthly';
  const initialStartDate = toDateInputValue(user.subscriptionStartedAtUtc) || toDateInputValue(new Date().toISOString());
  const initialEndDate = toDateInputValue(user.subscriptionCurrentPeriodEndUtc) || toDateInputValue(addDays(new Date(), 30).toISOString());

  overlay.innerHTML = `
    <div class="modal admin-user-detail">
      <div class="modal-header">
        <div>
          <div class="admin-detail-meta">
            ${providerIcon}
            <span>${esc(user.provider)}</span>
            ${blockedLabel}
            ${roleLabel}
          </div>
          <h2 class="admin-detail-email">${esc(user.email)}</h2>
        </div>
        <button class="modal-close">${icon('x', { size: 18 })}</button>
      </div>
        <div class="admin-detail-grid">
        <div class="admin-detail-item admin-detail-item--full">
          <span class="admin-detail-label">${t('admin.col.id')}</span>
          <span class="admin-detail-value admin-detail-mono">${esc(user.id)}</span>
        </div>
        <div class="admin-detail-item admin-detail-item--full">
          <span class="admin-detail-label">${t('admin.col.externalId')}</span>
          <span class="admin-detail-value admin-detail-mono">${esc(user.externalId)}</span>
        </div>
        <div class="admin-detail-item admin-detail-item--full">
          <span class="admin-detail-label">${t('admin.col.effectiveDisplayName')}</span>
          <span class="admin-detail-value">${esc(user.effectiveDisplayName)}</span>
        </div>
        <div class="admin-detail-item">
          <span class="admin-detail-label">${t('admin.col.createdAt')}</span>
          <span class="admin-detail-value">${createdAt}</span>
        </div>
        <div class="admin-detail-item">
          <span class="admin-detail-label">${t('admin.col.lastLoggedIn')}</span>
          <span class="admin-detail-value">${lastLoggedIn}</span>
        </div>
        <div class="admin-detail-item">
          <span class="admin-detail-label">${t('admin.col.blocked')}</span>
          <span class="admin-detail-value">${user.isBlocked ? t('admin.blocked') : t('admin.active')}</span>
        </div>
        <div class="admin-detail-item">
          <span class="admin-detail-label">${t('admin.col.onboarding')}</span>
          <span class="admin-detail-value">${onboardingText}</span>
        </div>
        <div class="admin-detail-item">
          <span class="admin-detail-label">${t('admin.col.business')}</span>
          <span class="admin-detail-value">${businessText}</span>
        </div>
      </div>
      <section class="admin-subscription-section">
        <div class="admin-subscription-header">
          <div>
            <h3 class="admin-subscription-title">${t('admin.subscription.currentSummary')}</h3>
            <p class="admin-subscription-copy">${t('admin.subscription.formDescription')}</p>
          </div>
        </div>
        <div class="admin-detail-grid admin-detail-grid--compact">
          <div class="admin-detail-item">
            <span class="admin-detail-label">${t('admin.subscription.currentPlan')}</span>
            <span class="admin-detail-value">${currentPlanLabel}</span>
          </div>
          <div class="admin-detail-item">
            <span class="admin-detail-label">${t('admin.subscription.interval')}</span>
            <span class="admin-detail-value">${currentIntervalLabel}</span>
          </div>
          <div class="admin-detail-item">
            <span class="admin-detail-label">${t('admin.subscription.status')}</span>
            <span class="admin-detail-value">${currentStatusLabel}</span>
          </div>
          <div class="admin-detail-item">
            <span class="admin-detail-label">${t('admin.subscription.provider')}</span>
            <span class="admin-detail-value">${currentProviderLabel}</span>
          </div>
          <div class="admin-detail-item">
            <span class="admin-detail-label">${t('admin.subscription.customerId')}</span>
            <span class="admin-detail-value admin-detail-mono">${currentCustomerIdLabel}</span>
          </div>
          <div class="admin-detail-item">
            <span class="admin-detail-label">${t('admin.subscription.startsAt')}</span>
            <span class="admin-detail-value">${currentStartLabel}</span>
          </div>
          <div class="admin-detail-item">
            <span class="admin-detail-label">${t('admin.subscription.endsAt')}</span>
            <span class="admin-detail-value">${currentEndLabel}</span>
          </div>
          <div class="admin-detail-item">
            <span class="admin-detail-label">${t('admin.subscription.startAccess')}</span>
            <span class="admin-detail-value">${subscriptionStartStatusLabel}</span>
          </div>
        </div>
      </section>
      <section class="admin-subscription-section">
        <div class="admin-subscription-header">
          <div>
            <h3 class="admin-subscription-title">${t('admin.subscription.formTitle')}</h3>
          </div>
        </div>
        ${isAdminRole ? `
          <div class="alert alert-info admin-delete-alert">
            <strong>${t('admin.subscription.protectedTitle')}</strong>
            <p class="admin-delete-alert-copy">${t('admin.subscription.protectedBody')}</p>
          </div>
        ` : `
          ${plansError ? `<div class="form-error admin-subscription-feedback">${esc(plansError)}</div>` : ''}
          ${plans.length ? `
          <form class="admin-subscription-form">
            <div class="form-error admin-subscription-feedback" data-form-error hidden></div>
            <div class="admin-subscription-form-grid">
              <div class="form-group">
                <label for="admin-subscription-plan">${t('admin.subscription.plan')}</label>
                <select id="admin-subscription-plan" name="planKey" required>
                  ${plans.map((plan) => `<option value="${esc(plan.key)}" ${plan.key === initialPlanKey ? 'selected' : ''}>${esc(plan.displayName)}</option>`).join('')}
                </select>
              </div>
              <div class="form-group">
                <label for="admin-subscription-interval">${t('admin.subscription.interval')}</label>
                <select id="admin-subscription-interval" name="billingInterval" required>
                  <option value="monthly" ${initialInterval === 'monthly' ? 'selected' : ''}>${t('admin.subscription.interval.monthly')}</option>
                  <option value="yearly" ${initialInterval === 'yearly' ? 'selected' : ''}>${t('admin.subscription.interval.yearly')}</option>
                </select>
              </div>
              <div class="form-group">
                <label for="admin-subscription-start">${t('admin.subscription.startDate')}</label>
                <input id="admin-subscription-start" name="startDate" type="date" value="${initialStartDate}" required>
              </div>
              <div class="form-group">
                <label for="admin-subscription-end">${t('admin.subscription.endDate')}</label>
                <input id="admin-subscription-end" name="endDate" type="date" value="${initialEndDate}" required>
              </div>
            </div>
            <div class="modal-actions">
              <button type="button" class="btn btn-outline" data-action="cancel">${t('common.cancel')}</button>
              <button type="submit" class="btn btn-primary">${t('common.save')}</button>
            </div>
          </form>
        ` : `
          <div class="admin-subscription-empty">${t('admin.subscription.noPlans')}</div>
        `}
        `}
      </section>
      ${isAdminRole ? `
        <section class="admin-subscription-section">
          <div class="alert alert-info admin-delete-alert">
            <strong>${t('admin.protectedAccount.title')}</strong>
            <p class="admin-delete-alert-copy">${t('admin.protectedAccount.body')}</p>
          </div>
        </section>
      ` : `
        <section class="admin-subscription-section">
          <div class="admin-subscription-header">
            <div>
              <h3 class="admin-subscription-title">${t('admin.userBlock.sectionTitle')}</h3>
              <p class="admin-subscription-copy">${t('admin.userBlock.sectionDescription')}</p>
            </div>
          </div>
          <div class="admin-danger-actions">
            <button type="button" class="btn ${user.isBlocked ? 'btn-outline' : 'btn-danger'}" data-action="toggle-block-user">
              ${user.isBlocked ? t('admin.userBlock.unblockButton') : t('admin.userBlock.blockButton')}
            </button>
          </div>
        </section>
        <section class="admin-subscription-section admin-danger-section">
          <div class="admin-subscription-header">
            <div>
              <h3 class="admin-subscription-title">${t('admin.userDelete.sectionTitle')}</h3>
              <p class="admin-subscription-copy">${t('admin.userDelete.sectionDescription')}</p>
            </div>
          </div>
          <div class="admin-danger-actions">
            <button type="button" class="btn btn-danger" data-action="delete-user">${t('admin.userDelete.openButton')}</button>
          </div>
        </section>
      `}
    </div>
  `;

  const close = (): void => overlay.remove();
  overlay.querySelector('.modal-close')?.addEventListener('click', close);
  overlay.querySelectorAll('[data-action="cancel"]').forEach((button) => button.addEventListener('click', close));
  overlay.querySelector('[data-action="delete-user"]')?.addEventListener('click', () => {
    close();
    openAdminUserDeleteDialog(user, onDeleted);
  });
  overlay.querySelector('[data-action="toggle-block-user"]')?.addEventListener('click', () => {
    close();
    openAdminUserBlockedDialog(user, onSaved);
  });
  overlay.addEventListener('click', (event) => { if (event.target === overlay) close(); });
  document.addEventListener('keydown', function onKey(event) {
    if (event.key === 'Escape') {
      close();
      document.removeEventListener('keydown', onKey);
    }
  });

  overlay.querySelector<HTMLFormElement>('.admin-subscription-form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    const formData = new FormData(form);
    const submitButton = form.querySelector<HTMLButtonElement>('[type="submit"]');
    const errorBox = form.querySelector<HTMLElement>('[data-form-error]');
    const planKey = String(formData.get('planKey') || '').trim();
    const billingInterval = formData.get('billingInterval') === 'yearly' ? 'yearly' : 'monthly';
    const startDate = String(formData.get('startDate') || '');
    const endDate = String(formData.get('endDate') || '');

    if (errorBox) {
      errorBox.hidden = true;
      errorBox.textContent = '';
    }

    if (!planKey || !startDate || !endDate) {
      showFormError(errorBox, t('admin.subscription.required'));
      return;
    }

    if (startDate >= endDate) {
      showFormError(errorBox, t('admin.subscription.periodError'));
      return;
    }

    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = t('common.saving');
    }

    try {
      await adminApi.setUserSubscription(user.id, {
        planKey,
        billingInterval,
        startsAtUtc: `${startDate}T00:00:00Z`,
        endsAtUtc: `${endDate}T23:59:59Z`,
      });
      close();
      await onSaved?.();
    } catch (error) {
      showFormError(
        errorBox,
        error instanceof ApiError ? error.message : t('admin.subscription.saveError'));
    } finally {
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = t('common.save');
      }
    }
  });

  document.body.appendChild(overlay);
}

function openAdminUserDeleteDialog(user: AdminUserDto, onDeleted?: () => Promise<void>): void {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal admin-user-delete-dialog">
      <div class="modal-header">
        <div>
          <h2>${t('admin.userDelete.dialogTitle')}</h2>
          <p class="modal-subtitle">${esc(user.effectiveDisplayName)} · ${esc(user.email)}</p>
        </div>
        <button class="modal-close">${icon('x', { size: 18 })}</button>
      </div>
      <div class="alert alert-danger admin-delete-alert">
        <strong>${t('admin.userDelete.warningTitle')}</strong>
        <p class="admin-delete-alert-copy">${t('admin.userDelete.warningIntro', { email: user.email })}</p>
      </div>
      <ul class="admin-delete-consequences">
        <li>${t('admin.userDelete.consequenceData')}</li>
        <li>${t('admin.userDelete.consequenceFiles')}</li>
        <li>${t('admin.userDelete.consequenceBilling')}</li>
        <li>${t('admin.userDelete.consequenceIrreversible')}</li>
      </ul>
      <div class="form-error admin-subscription-feedback" data-form-error hidden></div>
      <div class="modal-actions">
        <button type="button" class="btn btn-outline" data-action="cancel">${t('common.cancel')}</button>
        <button type="button" class="btn btn-danger" data-action="confirm-delete">${t('admin.userDelete.confirmLabel')}</button>
      </div>
    </div>
  `;

  const close = (): void => overlay.remove();
  const errorBox = overlay.querySelector<HTMLElement>('[data-form-error]');
  const confirmButton = overlay.querySelector<HTMLButtonElement>('[data-action="confirm-delete"]');

  overlay.querySelector('.modal-close')?.addEventListener('click', close);
  overlay.querySelector('[data-action="cancel"]')?.addEventListener('click', close);
  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) {
      close();
    }
  });
  overlay.querySelector('[data-action="confirm-delete"]')?.addEventListener('click', async () => {
    if (errorBox) {
      errorBox.hidden = true;
      errorBox.textContent = '';
    }

    if (confirmButton) {
      confirmButton.disabled = true;
      confirmButton.textContent = t('common.deleting');
    }

    try {
      await adminApi.deleteUser(user.id);
      close();
      await onDeleted?.();
    } catch (error) {
      if (errorBox) {
        errorBox.hidden = false;
        errorBox.textContent = error instanceof ApiError ? error.message : t('admin.userDelete.deleteError');
      }
    } finally {
      if (confirmButton) {
        confirmButton.disabled = false;
        confirmButton.textContent = t('admin.userDelete.confirmLabel');
      }
    }
  });

  document.body.appendChild(overlay);
}

export function openAdminPlatformResetDialog(onReset?: () => Promise<void>): void {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal admin-user-delete-dialog">
      <div class="modal-header">
        <div>
          <h2>${t('admin.platformReset.dialogTitle')}</h2>
          <p class="modal-subtitle">${t('admin.platformReset.dialogSubtitle')}</p>
        </div>
        <button class="modal-close">${icon('x', { size: 18 })}</button>
      </div>
      <div class="alert alert-danger admin-delete-alert">
        <strong>${t('admin.platformReset.warningTitle')}</strong>
        <p class="admin-delete-alert-copy">${t('admin.platformReset.warningIntro')}</p>
      </div>
      <ul class="admin-delete-consequences">
        <li>${t('admin.platformReset.consequenceData')}</li>
        <li>${t('admin.platformReset.consequenceFiles')}</li>
        <li>${t('admin.platformReset.consequencePreserved')}</li>
        <li>${t('admin.platformReset.consequenceIrreversible')}</li>
      </ul>
      <div class="form-group admin-reset-confirmation">
        <label for="admin-platform-reset-confirmation">${t('admin.platformReset.confirmationLabel')}</label>
        <input
          id="admin-platform-reset-confirmation"
          type="text"
          autocomplete="off"
          autocapitalize="off"
          spellcheck="false"
          placeholder="${t('admin.platformReset.confirmationPlaceholder')}"
        >
        <p class="form-help">${t('admin.platformReset.confirmationHelp')}</p>
      </div>
      <div class="form-error admin-subscription-feedback" data-form-error hidden></div>
      <div class="modal-actions">
        <button type="button" class="btn btn-outline" data-action="cancel">${t('common.cancel')}</button>
        <button type="button" class="btn btn-danger" data-action="confirm-reset" disabled>${t('admin.platformReset.confirmLabel')}</button>
      </div>
    </div>
  `;

  const close = (): void => overlay.remove();
  const errorBox = overlay.querySelector<HTMLElement>('[data-form-error]');
  const confirmationInput = overlay.querySelector<HTMLInputElement>('#admin-platform-reset-confirmation');
  const confirmButton = overlay.querySelector<HTMLButtonElement>('[data-action="confirm-reset"]');

  const syncConfirmationState = (): void => {
    if (confirmButton) {
      confirmButton.disabled = confirmationInput?.value.trim() !== 'rentomax';
    }
  };

  overlay.querySelector('.modal-close')?.addEventListener('click', close);
  overlay.querySelector('[data-action="cancel"]')?.addEventListener('click', close);
  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) {
      close();
    }
  });
  confirmationInput?.addEventListener('input', () => {
    if (errorBox) {
      errorBox.hidden = true;
      errorBox.textContent = '';
    }

    syncConfirmationState();
  });
  overlay.querySelector('[data-action="confirm-reset"]')?.addEventListener('click', async () => {
    if (!confirmationInput || confirmationInput.value.trim() !== 'rentomax') {
      showFormError(errorBox, t('admin.platformReset.confirmationMismatch'));
      syncConfirmationState();
      return;
    }

    if (errorBox) {
      errorBox.hidden = true;
      errorBox.textContent = '';
    }

    if (confirmButton) {
      confirmButton.disabled = true;
      confirmButton.textContent = t('common.processing');
    }

    try {
      await adminApi.resetPlatform({ confirmation: confirmationInput.value.trim() });
      close();
      await onReset?.();
    } catch (error) {
      showFormError(
        errorBox,
        error instanceof ApiError ? error.message : t('admin.platformReset.resetError'));
      syncConfirmationState();
    } finally {
      if (confirmButton) {
        confirmButton.textContent = t('admin.platformReset.confirmLabel');
      }
    }
  });

  document.body.appendChild(overlay);
  confirmationInput?.focus();
}

export function openAdminForceCancelSubscriptionsDialog(users: AdminUserDto[], onCompleted?: () => Promise<void>): void {
  openAdminForceCancelDialog({
    title: t('admin.bulkSubscription.dialogTitleSelected'),
    subtitle: t('admin.bulkSubscription.dialogSubtitleSelected', { count: users.length }),
    warningTitle: t('admin.bulkSubscription.warningTitle'),
    warningBody: t('admin.bulkSubscription.warningBodySelected'),
    confirmLabel: t('admin.bulkSubscription.confirmSelected'),
    requestFactory: () => adminApi.forceCancelPaidSubscriptions({
      userIds: users.map((user) => user.id),
      applyToAllPaidUsers: false,
    }),
    onCompleted,
  });
}

export function openAdminForceCancelAllPaidSubscriptionsDialog(onCompleted?: () => Promise<void>): void {
  openAdminForceCancelDialog({
    title: t('admin.bulkSubscription.dialogTitleAllPaid'),
    subtitle: t('admin.bulkSubscription.dialogSubtitleAllPaid'),
    warningTitle: t('admin.bulkSubscription.warningTitle'),
    warningBody: t('admin.bulkSubscription.warningBodyAllPaid'),
    confirmLabel: t('admin.bulkSubscription.confirmAllPaid'),
    requireConfirmation: true,
    requestFactory: (confirmation) => adminApi.forceCancelPaidSubscriptions({
      applyToAllPaidUsers: true,
      confirmation,
    }),
    onCompleted,
  });
}

function openAdminUserBlockedDialog(user: AdminUserDto, onSaved?: () => Promise<void>): void {
  const nextBlockedState = !user.isBlocked;
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal admin-user-delete-dialog">
      <div class="modal-header">
        <div>
          <h2>${nextBlockedState ? t('admin.userBlock.dialogTitleBlock') : t('admin.userBlock.dialogTitleUnblock')}</h2>
          <p class="modal-subtitle">${esc(user.effectiveDisplayName)} · ${esc(user.email)}</p>
        </div>
        <button class="modal-close">${icon('x', { size: 18 })}</button>
      </div>
      <div class="alert ${nextBlockedState ? 'alert-danger' : 'alert-info'} admin-delete-alert">
        <strong>${nextBlockedState ? t('admin.userBlock.warningTitleBlock') : t('admin.userBlock.warningTitleUnblock')}</strong>
        <p class="admin-delete-alert-copy">${nextBlockedState ? t('admin.userBlock.warningIntroBlock') : t('admin.userBlock.warningIntroUnblock')}</p>
      </div>
      <div class="form-error admin-subscription-feedback" data-form-error hidden></div>
      <div class="modal-actions">
        <button type="button" class="btn btn-outline" data-action="cancel">${t('common.cancel')}</button>
        <button type="button" class="btn ${nextBlockedState ? 'btn-danger' : 'btn-primary'}" data-action="confirm-block">
          ${nextBlockedState ? t('admin.userBlock.confirmBlock') : t('admin.userBlock.confirmUnblock')}
        </button>
      </div>
    </div>
  `;

  const close = (): void => overlay.remove();
  const errorBox = overlay.querySelector<HTMLElement>('[data-form-error]');
  const confirmButton = overlay.querySelector<HTMLButtonElement>('[data-action="confirm-block"]');

  overlay.querySelector('.modal-close')?.addEventListener('click', close);
  overlay.querySelector('[data-action="cancel"]')?.addEventListener('click', close);
  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) {
      close();
    }
  });
  overlay.querySelector('[data-action="confirm-block"]')?.addEventListener('click', async () => {
    if (errorBox) {
      errorBox.hidden = true;
      errorBox.textContent = '';
    }

    if (confirmButton) {
      confirmButton.disabled = true;
      confirmButton.textContent = t('common.processing');
    }

    try {
      await adminApi.setUserBlocked(user.id, { isBlocked: nextBlockedState });
      close();
      await onSaved?.();
    } catch (error) {
      if (errorBox) {
        errorBox.hidden = false;
        errorBox.textContent = error instanceof ApiError ? error.message : t('admin.userBlock.saveError');
      }
    } finally {
      if (confirmButton) {
        confirmButton.disabled = false;
        confirmButton.textContent = nextBlockedState ? t('admin.userBlock.confirmBlock') : t('admin.userBlock.confirmUnblock');
      }
    }
  });

  document.body.appendChild(overlay);
}

type ForceCancelDialogOptions = {
  title: string;
  subtitle: string;
  warningTitle: string;
  warningBody: string;
  confirmLabel: string;
  requireConfirmation?: boolean;
  requestFactory: (confirmation?: string) => Promise<unknown>;
  onCompleted?: () => Promise<void>;
};

function openAdminForceCancelDialog(options: ForceCancelDialogOptions): void {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal admin-user-delete-dialog">
      <div class="modal-header">
        <div>
          <h2>${esc(options.title)}</h2>
          <p class="modal-subtitle">${esc(options.subtitle)}</p>
        </div>
        <button class="modal-close">${icon('x', { size: 18 })}</button>
      </div>
      <div class="alert alert-danger admin-delete-alert">
        <strong>${esc(options.warningTitle)}</strong>
        <p class="admin-delete-alert-copy">${esc(options.warningBody)}</p>
      </div>
      ${options.requireConfirmation ? `
        <div class="form-group admin-reset-confirmation">
          <label for="admin-force-cancel-confirmation">${t('admin.bulkSubscription.confirmationLabel')}</label>
          <input
            id="admin-force-cancel-confirmation"
            type="text"
            autocomplete="off"
            autocapitalize="off"
            spellcheck="false"
            placeholder="${t('admin.bulkSubscription.confirmationPlaceholder')}"
          >
          <p class="form-help">${t('admin.bulkSubscription.confirmationHelp')}</p>
        </div>
      ` : ''}
      <div class="form-error admin-subscription-feedback" data-form-error hidden></div>
      <div class="modal-actions">
        <button type="button" class="btn btn-outline" data-action="cancel">${t('common.cancel')}</button>
        <button type="button" class="btn btn-danger" data-action="confirm-force-cancel" ${options.requireConfirmation ? 'disabled' : ''}>${esc(options.confirmLabel)}</button>
      </div>
    </div>
  `;

  const close = (): void => overlay.remove();
  const errorBox = overlay.querySelector<HTMLElement>('[data-form-error]');
  const confirmButton = overlay.querySelector<HTMLButtonElement>('[data-action="confirm-force-cancel"]');
  const confirmationInput = overlay.querySelector<HTMLInputElement>('#admin-force-cancel-confirmation');

  const syncConfirmationState = (): void => {
    if (confirmButton && options.requireConfirmation) {
      confirmButton.disabled = confirmationInput?.value.trim() !== 'rentomax';
    }
  };

  overlay.querySelector('.modal-close')?.addEventListener('click', close);
  overlay.querySelector('[data-action="cancel"]')?.addEventListener('click', close);
  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) {
      close();
    }
  });
  confirmationInput?.addEventListener('input', () => {
    if (errorBox) {
      errorBox.hidden = true;
      errorBox.textContent = '';
    }
    syncConfirmationState();
  });
  overlay.querySelector('[data-action="confirm-force-cancel"]')?.addEventListener('click', async () => {
    if (options.requireConfirmation && confirmationInput?.value.trim() !== 'rentomax') {
      showFormError(errorBox, t('admin.bulkSubscription.confirmationMismatch'));
      syncConfirmationState();
      return;
    }

    if (confirmButton) {
      confirmButton.disabled = true;
      confirmButton.textContent = t('common.processing');
    }

    try {
      await options.requestFactory(confirmationInput?.value.trim());
      close();
      await options.onCompleted?.();
    } catch (error) {
      showFormError(errorBox, error instanceof ApiError ? error.message : t('admin.bulkSubscription.saveError'));
      syncConfirmationState();
    } finally {
      if (confirmButton) {
        confirmButton.textContent = options.confirmLabel;
      }
    }
  });

  document.body.appendChild(overlay);
  confirmationInput?.focus();
}

function resolvePlanLabel(planKey: string | null | undefined, plans: BillingPlanDto[]): string {
  if (!planKey) {
    return t('admin.subscription.none');
  }

  return esc(plans.find((plan) => plan.key === planKey)?.displayName || planKey);
}

function resolveInitialPlanKey(user: AdminUserDto, plans: BillingPlanDto[]): string {
  if (user.subscriptionPlanKey && plans.some((plan) => plan.key === user.subscriptionPlanKey)) {
    return user.subscriptionPlanKey;
  }

  return plans[0]?.key || '';
}

function translateInterval(interval: string | null | undefined): string {
  if (interval === 'monthly') {
    return t('admin.subscription.interval.monthly');
  }

  if (interval === 'yearly') {
    return t('admin.subscription.interval.yearly');
  }

  return t('admin.subscription.none');
}

function translateSubscriptionStatus(status: string | null | undefined): string {
  switch (status) {
    case 'active':
      return t('admin.subscription.status.active');
    case 'past_due':
      return t('admin.subscription.status.pastDue');
    case 'canceled':
      return t('admin.subscription.status.canceled');
    case 'trialing':
      return t('admin.subscription.status.trialing');
    default:
      return status ? esc(status) : t('admin.subscription.none');
  }
}

function toDateInputValue(isoString: string | null | undefined): string {
  if (!isoString) {
    return '';
  }

  return isoString.slice(0, 10);
}

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setUTCDate(result.getUTCDate() + days);
  return result;
}

function showFormError(errorBox: HTMLElement | null, message: string): void {
  if (!errorBox) {
    return;
  }

  errorBox.textContent = message;
  errorBox.hidden = false;
}
